import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import DataGrid from '../../../../../common/DataGrid/DataGrid.jsx';
import { useTranslation } from 'react-i18next';
import IconButton from '../../../../../common/controls/IconButton/IconButton.jsx';
import { EditOutlined, FileSearchOutlined } from '@ant-design/icons';
import { useDataGridTextFilter } from '../../../../../common/DataGrid/filters/useDataGridTextFilter.jsx';
import { useOptionsDataGridFilter } from '../../../../../common/DataGrid/filters/DataGridFilters.jsx';
import { useDataGridDateRangeFilter } from '../../../../../common/DataGrid/filters/useDataGridDateRangeFilter.jsx';
import {
  dataGridDateSorter,
  dataGridTextSorter,
} from '../../../../../common/DataGrid/sorters/DataGridSorters.jsx';
import { Tooltip } from '../../../../../common/index.jsx';
import dayjs from 'dayjs';
import { DATETIME_FORMAT_DATETIME } from '../../../../../../utils/helpers/CommonHelper.jsx';

const PagesTable = ({
  wikiPages,
  wikiCategories,
  onDelete,
  onDetailClick,
  onEditClick,
  secondaryCondition,
  deleteCondition,
}) => {
  const { t } = useTranslation();
  const titleFilterProps = useDataGridTextFilter('title');
  const authorFilterProps = useDataGridTextFilter('author_name');
  const updatedAtFilterProps = useDataGridDateRangeFilter('updated_at');

  const categoryOptions = useMemo(() => {
    if (!Array.isArray(wikiCategories)) return {};

    return wikiCategories.reduce((acc, category) => {
      acc[category.name] = category.name;
      return acc;
    }, {});
  }, [wikiCategories]);

  const categoryFilterProps = useOptionsDataGridFilter(
    'category_name',
    categoryOptions,
  );

  // Title takes the remaining space, the other columns have fixed widths that fit their content
  const columns = [
    {
      title: t('Názov'),
      dataIndex: 'title',
      ellipsis: { showTitle: false },
      sorter: dataGridTextSorter('title'),
      render: (title) => (
        <Tooltip tooltip={title}>
          <span>{title || '–'}</span>
        </Tooltip>
      ),
      ...titleFilterProps,
    },
    {
      title: t('Kategória'),
      dataIndex: 'category_name',
      width: 160,
      ellipsis: true,
      sorter: dataGridTextSorter('category_name'),
      render: (categoryName) => categoryName || '–',
      ...categoryFilterProps,
    },
    {
      title: t('Autor'),
      dataIndex: 'author_name',
      width: 220,
      ellipsis: true,
      responsive: ['md'],
      sorter: dataGridTextSorter('author_name'),
      render: (authorName) => authorName || '–',
      ...authorFilterProps,
    },
    {
      title: t('Dátum úpravy'),
      dataIndex: 'updated_at',
      width: 160,
      responsive: ['lg'],
      sorter: dataGridDateSorter('updated_at'),
      render: (date) =>
        date ? dayjs(date).format(DATETIME_FORMAT_DATETIME) : '–',
      ...updatedAtFilterProps,
    },
  ];

  return (
    <>
      <DataGrid
        rowKey={(wikiPage) => wikiPage.id}
        columns={columns}
        dataSource={wikiPages || []}
        onDelete={onDelete}
        customSettings={(row) => (
          <>
            {secondaryCondition?.(row) ? (
              <IconButton
                icon={<EditOutlined />}
                tooltip={t('Upraviť')}
                onClick={(e) => {
                  e.stopPropagation();
                  onEditClick?.(row);
                }}
              />
            ) : null}
            <IconButton
              icon={<FileSearchOutlined />}
              tooltip={t('Zobraziť detail')}
              onClick={(e) => {
                e.stopPropagation();
                onDetailClick?.(row);
              }}
            />
          </>
        )}
        permissionsBasic={'wiki-pages'}
        uniqueTableId={'dynamic-wiki-pages-table'}
        onRowClick={(row) => {
          if (secondaryCondition?.(row)) {
            onEditClick?.(row);
          } else {
            onDetailClick?.(row);
          }
        }}
        onRowDetailClick={onDetailClick}
        deleteCondition={deleteCondition}
      />
    </>
  );
};

PagesTable.propTypes = {
  wikiPages: PropTypes.array,
  wikiCategories: PropTypes.array,
  onDelete: PropTypes.func,
  onDetailClick: PropTypes.func,
  onEditClick: PropTypes.func,
  onPreviewClick: PropTypes.func,
  secondaryCondition: PropTypes.func,
  deleteCondition: PropTypes.func,
};

export default PagesTable;
